// import * as React from 'react';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';

import * as userLib from 'js/lib/user';

import type {
  ProgramSwitcherSelectionsV1_ProgramSwitcherSelectionsV1Resource_get_program_program_ProgramsV1_degreeProgramMember as DegreeProgram,
  ProgramSwitcherSelectionsV1_ProgramSwitcherSelectionsV1Resource_get_program_program_ProgramsV1_enterpriseProgramMember as EnterpriseProgram,
  ProgramSwitcherSelectionsV1,
  ProgramSwitcherSelectionsV1Variables,
} from 'bundles/clips/components/short-form-content/__generated__/ProgramSwitcherSelectionsV1';
import programSwitcherSelections from 'bundles/clips/components/short-form-content/programSwitcherSelectionsQuery';

// Type guard
const isEnterpriseProgram = (program: EnterpriseProgram | DegreeProgram): program is EnterpriseProgram => {
  return (program as EnterpriseProgram).enterpriseProgram !== undefined;
};

const getOrgIdFromGraphqlData = (gqlData?: ProgramSwitcherSelectionsV1) => {
  const program = gqlData?.ProgramSwitcherSelectionsV1Resource.get?.program?.program;
  if (program && isEnterpriseProgram(program)) {
    return program.enterpriseProgram.thirdPartyOrganizationId;
  }
  return undefined;
};

// get selected program ID and organzation ID from program switcher API
export const useSelectedProgram = (): {
  programId?: string | null;
  organizationId?: string | null;
  loading: boolean;
} => {
  const user = userLib.isAuthenticatedUser() ? userLib.get() : null;

  const { data: programSwitcherData, loading } = useQuery<
    ProgramSwitcherSelectionsV1,
    ProgramSwitcherSelectionsV1Variables
  >(programSwitcherSelections, {
    variables: { userId: String(user?.id) },
    skip: !user,
    ssr: true,
    errorPolicy: 'all', // Dont want to block render in case of an error.
    context: { clientName: 'gatewayGql' },

    onError: (errorObj) => {
      Sentry.captureException(errorObj, {
        extra: { message: 'Error fetching program switcher in useSelectedProgram.' },
      });
    },
  });

  const programId = programSwitcherData?.ProgramSwitcherSelectionsV1Resource.get?.programId;
  const organizationId = getOrgIdFromGraphqlData(programSwitcherData);

  return {
    loading,
    organizationId,
    programId,
  };
};
